/**
 * <ShopGestureSheet> — la fiche d'un geste de la boutique, et l'accusé de son achat (template).
 *
 * Une feuille, deux étapes, contrôlées par `step` :
 *  - `detail` : la fiche d'aujourd'hui (l'image, la phrase, le texte long, le prix), plus ce que
 *    l'achat va dépenser en face de ce que l'élève a (« Tes crédits »). C'est ICI que l'achat se
 *    confirme : « Acheter » est le seul bouton qui dépense des pétales. Sans assez de pétales, le
 *    bouton est inerte et la feuille le dit ; pendant l'achat il tourne et la feuille ne se ferme
 *    plus.
 *  - `purchased` : l'accusé. Ce que l'ancien code déroule en quatre écrans de félicitations tient
 *    en une étape : « Merci », le geste financé, le mot du geste que Tina dit dans sa bulle,
 *    combien l'élève en a fait en tout, la quête que le geste fait avancer (sous l'aurore : celle
 *    qui la porte vient d'agir ; sa barre part du compte d'avant et le rejoint, et la toucher ouvre
 *    « Mes quêtes »), le titre qu'il vient de donner le cas échéant, posé en médaille sous une pluie
 *    de confettis, « Continuer ».
 *
 * Un achat qui ne passe pas referme la feuille, comme aujourd'hui : c'est la page qui le dit
 * (`<Snackbar>`).
 *
 * Celui qui porte la quête se montre dans l'accusé (il a agi : sa quête avance), par sa tête.
 * Jamais dans la fiche : une boîte de dialogue ne porte pas de champion.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Confetti } from "../../atoms/Confetti"
import { Card } from "../../molecules/Card"
import { MessageRow } from "../../molecules/MessageRow"
import { Modal } from "../../molecules/Modal"
import { SummaryRow } from "../../molecules/SummaryRow"
import { TitleMedal } from "../../molecules/TitleMedal"
import { QuestCard, QuestChampion } from "../../organisms/QuestCard"
import {
    GESTURE_IMAGE_SIZE,
    GestureImage,
    SHOP_GESTURE_TEXTS,
    type UIShopGesture,
} from "../../organisms/ShopGestureCard"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { formatAmount } from "../../utils/gameAmount"
import { type UIShopNewTitle, type UIShopPurchaseOutcome } from "../../../ports/primary/ui/types"

export type { UIShopNewTitle, UIShopPurchaseOutcome }

export type ShopGestureSheetStep = "closed" | "detail" | "purchased"

export type ShopGestureSheetProps = {
    step: ShopGestureSheetStep
    /** Le geste ouvert. `null` : la feuille ne se monte pas. */
    gesture: UIShopGesture | null
    /** Les pétales de l'élève. */
    petals: number
    /** Combien de fois il a déjà fait ce geste. Défaut 0. */
    purchasedCount?: number
    /** L'achat est parti : le bouton tourne, la feuille ne se ferme plus. Défaut `false`. */
    purchasing?: boolean
    onBuy: () => void
    /** La croix, et « Continuer » sur l'accusé. */
    onClose: () => void
    /** Ce que l'accusé annonce. */
    outcome?: UIShopPurchaseOutcome | null
    /** « Partager mes statistiques » : sur l'application seulement. Absent : pas de bouton. */
    onShare?: () => void
    /** « Choisir ce titre à la place de… ». Absent : pas de geste. */
    onChooseNewTitle?: () => void
    /** Le changement de titre est parti. Défaut `false`. */
    choosingNewTitle?: boolean
    /** Toucher la quête de l'accusé : « Mes quêtes ». Absent : la quête ne presse pas. */
    onOpenQuest?: () => void
    /** La barre de la quête vient de rejoindre son compte (l'écran y joue son son). */
    onQuestFilled?: () => void
    /** Le libellé du bouton d'achat. Défaut « Acheter », le mot d'aujourd'hui. */
    buyLabel?: string
    testID?: string
}

/** L'image du geste dans sa fiche : plus grande que sur sa carte. */
const SHEET_IMAGE_SIZE = 96

const TEXTS = {
    balance: "Tes crédits",
    thanks: "Merci",
    financed: "Tu viens de financer un geste",
    bravo: frenchSpacing("Bravo pour ce nouveau geste !"),
    newTitle: frenchSpacing("Félicitations ! Tu as remporté le titre"),
    titleApplied: "Ton profil a été mis à jour avec ce titre",
    chooseTitle: (current: string) => `Choisir ce titre à la place ${elide(current, "de")}`,
    continue: "Continuer",
    share: "Partager mes statistiques",
}

export function ShopGestureSheet(props: ShopGestureSheetProps) {
    const {
        step,
        gesture,
        petals,
        purchasedCount = 0,
        purchasing = false,
        onBuy,
        onClose,
        outcome,
        onShare,
        onChooseNewTitle,
        choosingNewTitle = false,
        onOpenQuest,
        onQuestFilled,
        buyLabel = SHOP_GESTURE_TEXTS.buy,
        testID,
    } = props

    if (gesture == null) return null

    const { name, shortDescription, description, price } = gesture
    const canAfford = petals >= price
    const hasDescription = description != null && description !== ""

    if (step === "purchased") {
        const quest = outcome?.quest ?? null
        const newTitle = outcome?.newTitle ?? null
        const canChooseTitle =
            newTitle != null && !newTitle.applied && onChooseNewTitle != null && (newTitle.currentTitle ?? "") !== ""

        return (
            <Modal
                testID={testID}
                visible
                onClose={onClose}
                title={TEXTS.thanks}
                steps={{ step: 1, total: 1 }}
                fullScreenOnSmall
                sizeToContent
                footer={
                    <Stack gap="2">
                        <Button label={TEXTS.continue} fullWidth onPress={onClose} />
                        {onShare != null && (
                            <Button
                                label={TEXTS.share}
                                variant="outline"
                                iconName="share-alt"
                                fullWidth
                                onPress={onShare}
                            />
                        )}
                    </Stack>
                }
            >
                <Stack gap="4">
                    <Row gap="3" align="center">
                        <GestureImage gesture={gesture} size={GESTURE_IMAGE_SIZE} />
                        <Stack gap="0" style={styles.body}>
                            <Text size="text-md" color="text-muted">
                                {TEXTS.financed}
                            </Text>
                            <Text size="text-lg" weight="bold">
                                {name}
                            </Text>
                        </Stack>
                    </Row>

                    {outcome?.congratsMessage != null && outcome.congratsMessage !== "" && (
                        // La bulle a la surface de la feuille : posée dans l'encart de la quête, elle se détache.
                        <Card p="3" elevation="none" background="surface-subtle">
                            <MessageRow
                                message={{ id: "shop-congrats", author: "other", text: outcome.congratsMessage }}
                                speaker="agent"
                                tail
                                champion="tina"
                            />
                        </Card>
                    )}

                    {outcome != null && (
                        <Stack gap="1">
                            <Text size="text-lg" weight="bold" color="primary">
                                {TEXTS.bravo}
                            </Text>
                            <Text size="text-md">{frenchSpacing(outcome.totalLabel)}</Text>
                        </Stack>
                    )}

                    {quest != null && (
                        <QuestCard
                            quest={quest}
                            champion={<QuestChampion name={quest.champion} />}
                            embedded
                            aurora
                            fromCount={outcome?.questFromCount}
                            onFilled={onQuestFilled}
                            onPress={onOpenQuest}
                        />
                    )}

                    {newTitle != null && (
                        <Stack gap="2" align="center" style={styles.newTitle}>
                            <Confetti />
                            <Text size="text-md" center>
                                {TEXTS.newTitle}
                            </Text>
                            <TitleMedal title={newTitle.title} layout="stacked" />
                            {newTitle.applied && (
                                <Text size="text-sm" color="text-muted">
                                    {TEXTS.titleApplied}
                                </Text>
                            )}
                            {canChooseTitle && (
                                <TextButton
                                    label={TEXTS.chooseTitle(newTitle.currentTitle ?? "")}
                                    loading={choosingNewTitle}
                                    onPress={onChooseNewTitle}
                                />
                            )}
                        </Stack>
                    )}
                </Stack>
            </Modal>
        )
    }

    return (
        <Modal
            testID={testID}
            visible={step === "detail"}
            onClose={onClose}
            title={name}
            // La tête d'une étape : un nom de geste long passe à la ligne au lieu de buter sur la croix.
            steps={{ step: 1, total: 1 }}
            fullScreenOnSmall
            sizeToContent
            dismissable={!purchasing}
            footer={
                <Stack gap="2">
                    {!canAfford && (
                        <Text size="text-sm" color="text-muted" center>
                            {SHOP_GESTURE_TEXTS.notEnough}
                        </Text>
                    )}
                    <Button label={buyLabel} fullWidth disabled={!canAfford} loading={purchasing} onPress={onBuy} />
                </Stack>
            }
        >
            <Stack gap="4">
                <Row gap="3" align="center">
                    <GestureImage gesture={gesture} size={SHEET_IMAGE_SIZE} />
                    <Stack gap="2" align="start" style={styles.body}>
                        <Text size="text-md" color="text-muted">
                            {frenchSpacing(shortDescription)}
                        </Text>
                        {purchasedCount > 0 && (
                            <Badge
                                text={SHOP_GESTURE_TEXTS.purchased(purchasedCount)}
                                variant="success"
                                tinted
                                iconName="check"
                            />
                        )}
                    </Stack>
                </Row>

                {hasDescription && <Text size="text-md">{frenchSpacing(description)}</Text>}

                <Card p="3" elevation="none" background="surface-subtle">
                    <Stack gap="2">
                        <SummaryRow label={name} value={formatAmount(price, "petals")} emphasis />
                        <SummaryRow label={TEXTS.balance} value={formatAmount(petals, "petals")} />
                    </Stack>
                </Card>
            </Stack>
        </Modal>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flex: 1, flexShrink: 1, minWidth: 0 },
    /** La pluie tombe sur la médaille : elle s'y pose par-dessus. */
    newTitle: { position: "relative" },
}))
